import React, { useMemo } from "react";
import { Box, MenuItem, Select } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TargetStatus, TargetType } from "./types";
import {
  STATUS_COLUMN_ORDER,
  TYPE_CONFIG,
  STATUS_CONFIG,
  PRIMARY_COLOR,
} from "./constants";
import { ObjectiveRow, getObjectiveDisplayText } from "./ObjectivesModes";
import { tokens } from "../../theme/tokens";

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterPanelProps {
  filterStatus: TargetStatus | "";
  filterType: TargetType | "";
  filterTaskId: string;
  filterPoiId: string;
  search: string;
  setFilterStatus: (v: TargetStatus | "") => void;
  setFilterType: (v: TargetType | "") => void;
  setFilterTaskId: (v: string) => void;
  setFilterPoiId: (v: string) => void;
  taskOptions: { taskId: string; title: string }[];
  poiOptions: { poiId: string; name: string }[];
  allRows: ObjectiveRow[];
  panelRef: React.RefObject<HTMLDivElement>;
  onDone: () => void;
}

const DROPDOWN_LABEL: React.CSSProperties = {
  fontSize: "10px",
  fontWeight: 700,
  color: tokens.color.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  width: "44px",
  flexShrink: 0,
};

const CHIP_ROW = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  flexWrap: "wrap" as const,
  mb: "8px",
  "&:last-of-type": { mb: 0 },
};

export const FilterPill: React.FC<{ label: string; onRemove: () => void }> = ({
  label,
  onRemove,
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      fontSize: "10px",
      fontWeight: 600,
      color: PRIMARY_COLOR,
      backgroundColor: `${PRIMARY_COLOR}15`,
      border: `1px solid ${PRIMARY_COLOR}40`,
      borderRadius: "20px",
      padding: "4px 5px 4px 8px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      lineHeight: 1,
    }}
  >
    {label}
    <button
      type="button"
      onClick={onRemove}
      style={{
        display: "flex",
        alignItems: "center",
        background: "none",
        border: "none",
        padding: "1px",
        cursor: "pointer",
        color: PRIMARY_COLOR,
        opacity: 0.7,
        lineHeight: 1,
      }}
    >
      <CloseIcon style={{ fontSize: "9px" }} />
    </button>
  </Box>
);

export const FilterChip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      fontSize: "12px",
      fontWeight: active ? 600 : 400,
      padding: "6px 14px",
      height: "26px",
      borderRadius: "20px",
      border: active ? "none" : `1px solid ${tokens.color.border.default}`,
      backgroundColor: active ? PRIMARY_COLOR : tokens.color.surface.default,
      color: active ? tokens.color.surface.default : tokens.color.text.primary,
      cursor: "pointer",
      whiteSpace: "nowrap",
      flexShrink: 0,
      lineHeight: 1,
      display: "inline-flex",
      alignItems: "center",
    }}
  >
    {children}
  </button>
);

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filterStatus,
  filterType,
  filterTaskId,
  filterPoiId,
  search,
  setFilterStatus,
  setFilterType,
  setFilterTaskId,
  setFilterPoiId,
  taskOptions,
  poiOptions,
  allRows,
  panelRef,
  onDone,
}) => {
  // Each base set excludes the filter for its own dimension so chip counts
  // show "how many results if you pick this value".
  const baseForStatus = useMemo(
    () =>
      allRows.filter((r) => {
        if (filterType && r.target.type !== filterType) return false;
        if (filterTaskId && r.taskId !== filterTaskId) return false;
        if (filterPoiId && r.poiId !== filterPoiId) return false;
        if (
          search &&
          !getObjectiveDisplayText(r.target)
            .toLowerCase()
            .includes(search.toLowerCase())
        )
          return false;
        return true;
      }),
    [allRows, filterType, filterTaskId, filterPoiId, search],
  );

  const baseForType = useMemo(
    () =>
      allRows.filter((r) => {
        if (filterStatus && r.target.status !== filterStatus) return false;
        if (filterTaskId && r.taskId !== filterTaskId) return false;
        if (filterPoiId && r.poiId !== filterPoiId) return false;
        if (
          search &&
          !getObjectiveDisplayText(r.target)
            .toLowerCase()
            .includes(search.toLowerCase())
        )
          return false;
        return true;
      }),
    [allRows, filterStatus, filterTaskId, filterPoiId, search],
  );

  const baseForTask = useMemo(
    () =>
      allRows.filter((r) => {
        if (filterStatus && r.target.status !== filterStatus) return false;
        if (filterType && r.target.type !== filterType) return false;
        if (filterPoiId && r.poiId !== filterPoiId) return false;
        if (
          search &&
          !getObjectiveDisplayText(r.target)
            .toLowerCase()
            .includes(search.toLowerCase())
        )
          return false;
        return true;
      }),
    [allRows, filterStatus, filterType, filterPoiId, search],
  );

  const baseForPoi = useMemo(
    () =>
      allRows.filter((r) => {
        if (filterStatus && r.target.status !== filterStatus) return false;
        if (filterType && r.target.type !== filterType) return false;
        if (filterTaskId && r.taskId !== filterTaskId) return false;
        if (
          search &&
          !getObjectiveDisplayText(r.target)
            .toLowerCase()
            .includes(search.toLowerCase())
        )
          return false;
        return true;
      }),
    [allRows, filterStatus, filterType, filterTaskId, search],
  );

  const statusCounts = useMemo(
    () =>
      STATUS_COLUMN_ORDER.reduce<Record<string, number>>((acc, s) => {
        acc[s] = baseForStatus.filter((r) => r.target.status === s).length;
        return acc;
      }, {}),
    [baseForStatus],
  );

  const typeCounts = useMemo(() => {
    const counts: Partial<Record<TargetType, number>> = {};
    baseForType.forEach((r) => {
      counts[r.target.type] = (counts[r.target.type] ?? 0) + 1;
    });
    return counts;
  }, [baseForType]);

  const poiCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    baseForPoi.forEach((r) => {
      counts[r.poiId] = (counts[r.poiId] ?? 0) + 1;
    });
    return counts;
  }, [baseForPoi]);

  const allTypesPresent = useMemo(() => {
    const s = new Set<TargetType>();
    allRows.forEach((r) => s.add(r.target.type));
    return s;
  }, [allRows]);

  const typeOptions: FilterOption[] = Object.entries(TYPE_CONFIG)
    .filter(([value]) => allTypesPresent.has(value as TargetType))
    .map(([value, cfg]) => ({
      value,
      label: cfg.label,
      count: typeCounts[value as TargetType] ?? 0,
    }));

  return (
    <Box
      ref={panelRef as React.RefObject<HTMLDivElement>}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        px: "16px",
        pt: "12px",
        pb: "10px",
        borderBottom: `1px solid ${tokens.color.border.default}`,
        backgroundColor: tokens.color.surface.default,
        boxShadow: "none",
      }}
    >
      {/* POI — only shown when there are multiple POIs */}
      {poiOptions.length > 1 && (
        <Box sx={CHIP_ROW}>
          <span style={DROPDOWN_LABEL}>POI</span>
          <FilterChip active={!filterPoiId} onClick={() => setFilterPoiId("")}>
            All
          </FilterChip>
          {poiOptions.map((o) => (
            <FilterChip
              key={o.poiId}
              active={filterPoiId === o.poiId}
              onClick={() =>
                setFilterPoiId(filterPoiId === o.poiId ? "" : o.poiId)
              }
            >
              {o.name} ({poiCounts[o.poiId] ?? 0})
            </FilterChip>
          ))}
        </Box>
      )}

      {/* Status */}
      <Box sx={CHIP_ROW}>
        <span style={DROPDOWN_LABEL}>Status</span>
        <FilterChip active={!filterStatus} onClick={() => setFilterStatus("")}>
          All
        </FilterChip>
        {STATUS_COLUMN_ORDER.map((s) => {
          const cfg = STATUS_CONFIG[s];
          return (
            <FilterChip
              key={s}
              active={filterStatus === s}
              onClick={() => setFilterStatus(filterStatus === s ? "" : s)}
            >
              {cfg.label} ({statusCounts[s] ?? 0})
            </FilterChip>
          );
        })}
      </Box>

      {/* Type */}
      <Box sx={CHIP_ROW}>
        <span style={DROPDOWN_LABEL}>Type</span>
        <FilterChip active={!filterType} onClick={() => setFilterType("")}>
          All
        </FilterChip>
        {typeOptions.map((o) => (
          <FilterChip
            key={o.value}
            active={filterType === o.value}
            onClick={() =>
              setFilterType(
                filterType === o.value ? "" : (o.value as TargetType),
              )
            }
          >
            {o.label} ({o.count})
          </FilterChip>
        ))}
      </Box>

      {/* Task — MUI Select so menu items match app styling */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "8px" }}
      >
        <span style={DROPDOWN_LABEL}>Task</span>
        <Select
          size="small"
          value={filterTaskId}
          onChange={(e) => setFilterTaskId(e.target.value as string)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: "8px",
                border: `1px solid ${tokens.color.border.default}`,
                boxShadow: "none",
                mt: "4px",
              },
            },
          }}
          sx={{
            fontSize: "12px",
            fontWeight: filterTaskId ? 600 : 500,
            height: "28px",
            minWidth: "160px",
            color: filterTaskId ? PRIMARY_COLOR : tokens.color.text.primary,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: filterTaskId ? PRIMARY_COLOR : tokens.color.border.default,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: filterTaskId ? PRIMARY_COLOR : tokens.color.border.strong,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: PRIMARY_COLOR,
              borderWidth: "1px",
            },
            "& .MuiSelect-select": { py: "4px" },
          }}
        >
          <MenuItem value="" sx={{ fontSize: "12px", color: tokens.color.text.secondary }}>
            All tasks
          </MenuItem>
          {taskOptions.map((o) => {
            const count = baseForTask.filter(
              (r) => r.taskId === o.taskId,
            ).length;
            return (
              <MenuItem
                key={o.taskId}
                value={o.taskId}
                sx={{ fontSize: "12px", color: tokens.color.text.primary }}
              >
                {o.title} ({count})
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      {/* Footer: Clear Filters + Done */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: "10px",
        }}
      >
        <button
          type="button"
          onClick={() => {
            onDone();
            setFilterStatus("");
            setFilterType("");
            setFilterTaskId("");
            setFilterPoiId("");
          }}
          style={{
            fontSize: "12px",
            fontWeight: 500,
            color: tokens.color.text.secondary,
            backgroundColor: "transparent",
            border: "none",
            padding: "6px 0",
            cursor: "pointer",
          }}
        >
          Clear Filters
        </button>
        <button
          type="button"
          onClick={onDone}
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: tokens.color.surface.default,
            backgroundColor: PRIMARY_COLOR,
            border: "none",
            borderRadius: "7px",
            padding: "6px 18px",
            cursor: "pointer",
          }}
        >
          Done
        </button>
      </Box>
    </Box>
  );
};
